import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { API_URL } from '../../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Video,
  Users,
  Loader2,
  CheckCircle,
  BookOpen,
  Gamepad2,
  PartyPopper,
  GraduationCap,
  Sparkles,
  ChevronRight
} from 'lucide-react';

const eventTypeIcons = {
  bible_study: BookOpen,
  bible_game: Gamepad2,
  workshop: GraduationCap,
  party: PartyPopper,
  other: Sparkles
};

const eventTypeLabels = {
  bible_study: 'Bible Study',
  bible_game: 'Bible Game',
  workshop: 'Workshop',
  party: 'Party',
  other: 'Event'
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showRSVP, setShowRSVP] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // RSVP form state
  const [rsvpData, setRsvpData] = useState({
    name: '',
    email: '',
    phone: '',
    num_attendees: 1,
    notes: ''
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/events`);
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
    }
    setLoading(false);
  };

  const handleRSVP = async (e) => {
    e.preventDefault();
    
    if (!rsvpData.name || !rsvpData.email) {
      toast.error('Please fill in your name and email');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/events/${selectedEvent.event_id}/rsvp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rsvpData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        setShowRSVP(false);
        setSelectedEvent(null);
        setRsvpData({ name: '', email: '', phone: '', num_attendees: 1, notes: '' });
      } else {
        toast.error(data.message || 'Failed to RSVP');
      }
    } catch (err) {
      toast.error('Failed to submit RSVP. Please try again.');
    }
    setSubmitting(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const upcomingEvents = events.filter(e => new Date(e.event_date) >= new Date());
  const pastEvents = events.filter(e => new Date(e.event_date) < new Date());

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-emerald-50">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="relative py-16 overflow-hidden">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-10 right-10 w-32 h-32 bg-amber-200 rounded-full blur-3xl" />
            <div className="absolute bottom-10 left-20 w-40 h-40 bg-emerald-200 rounded-full blur-3xl" />
          </div>
          
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <Link to="/" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mb-8 group">
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
              Back to Home
            </Link>
            
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-amber-100 rounded-2xl">
                <Calendar className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-serif font-bold text-slate-800">
                Events
              </h1>
            </div>
            
            <p className="text-xl text-slate-600 leading-relaxed">
              Join us for Bible studies, games, workshops, and community gatherings. 
              We'd love to see you there!
            </p>
          </div>
        </section>

        {/* Upcoming Events */}
        <section className="py-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-serif font-bold text-slate-800 mb-8">
              Upcoming Events
            </h2>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : upcomingEvents.length === 0 ? (
              <div className="bg-white rounded-3xl border p-12 text-center">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-800 mb-2">No Upcoming Events</h3>
                <p className="text-slate-500">
                  Check back soon or subscribe to our newsletter to be notified of new events!
                </p>
              </div>
            ) : (
              <div className="grid gap-6">
                {upcomingEvents.map((event) => {
                  const TypeIcon = eventTypeIcons[event.event_type] || Sparkles;
                  return (
                    <div
                      key={event.event_id}
                      className="bg-white rounded-3xl shadow-lg border overflow-hidden hover:shadow-xl transition-shadow"
                    >
                      <div className="md:flex">
                        {/* Date Badge */}
                        <div className="md:w-32 bg-gradient-to-br from-emerald-500 to-teal-500 p-6 text-white text-center flex flex-col justify-center">
                          <div className="text-4xl font-bold">
                            {new Date(event.event_date).getDate()}
                          </div>
                          <div className="text-emerald-100 uppercase text-sm font-medium">
                            {new Date(event.event_date).toLocaleDateString('en-GB', { month: 'short' })}
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 p-6">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                                  event.event_type === 'bible_study' ? 'bg-blue-100 text-blue-700' :
                                  event.event_type === 'bible_game' ? 'bg-purple-100 text-purple-700' :
                                  event.event_type === 'workshop' ? 'bg-amber-100 text-amber-700' :
                                  event.event_type === 'party' ? 'bg-pink-100 text-pink-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  <TypeIcon className="w-3 h-3" />
                                  {eventTypeLabels[event.event_type] || 'Event'}
                                </span>
                                {event.location_type === 'online' && (
                                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-xs font-medium">
                                    <Video className="w-3 h-3" />
                                    Online
                                  </span>
                                )}
                              </div>
                              
                              <h3 className="text-xl font-bold text-slate-800 mb-2">{event.title}</h3>
                              <p className="text-slate-600 text-sm mb-4 line-clamp-2">{event.description}</p>
                              
                              <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                                <div className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {formatTime(event.event_date)}
                                </div>
                                <div className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {event.location_type === 'online' ? 'Online' : (event.location_address || 'TBA')}
                                </div>
                                {event.max_participants && (
                                  <div className="flex items-center gap-1">
                                    <Users className="w-4 h-4" />
                                    {event.rsvp_count || 0}/{event.max_participants} spots
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex flex-col gap-2">
                              {event.is_rsvp_required && (
                                <Button
                                  onClick={() => {
                                    setSelectedEvent(event);
                                    setShowRSVP(true);
                                  }}
                                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                                >
                                  RSVP <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Past Events */}
        {pastEvents.length > 0 && (
          <section className="py-12 bg-slate-50">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-serif font-bold text-slate-800 mb-8">
                Past Events
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                {pastEvents.slice(0, 6).map((event) => {
                  const TypeIcon = eventTypeIcons[event.event_type] || Sparkles;
                  return (
                    <div
                      key={event.event_id}
                      className="bg-white rounded-2xl border p-4 opacity-75"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                          <TypeIcon className="w-6 h-6 text-slate-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-slate-800">{event.title}</h3>
                          <p className="text-sm text-slate-500">{formatDate(event.event_date)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Stay Updated */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-3xl p-8 sm:p-12 text-center text-white">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-amber-200" />
              <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-4">
                Never Miss an Event
              </h2>
              <p className="text-amber-100 mb-8 max-w-2xl mx-auto">
                Subscribe to our newsletter to receive notifications about upcoming events, Bible studies, and community gatherings.
              </p>
              <Link to="/#newsletter">
                <Button className="bg-white text-amber-600 hover:bg-amber-50 rounded-full px-8 py-6">
                  Subscribe to Newsletter
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* RSVP Modal */}
      {showRSVP && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h3 className="text-2xl font-serif font-bold text-slate-800">RSVP to Event</h3>
              <p className="text-slate-600">{selectedEvent.title}</p>
              <p className="text-sm text-slate-500 mt-1">{formatDate(selectedEvent.event_date)} at {formatTime(selectedEvent.event_date)}</p>
            </div>
            
            <form onSubmit={handleRSVP} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Your Name *</label>
                <Input
                  value={rsvpData.name}
                  onChange={(e) => setRsvpData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Full name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                <Input
                  type="email"
                  value={rsvpData.email}
                  onChange={(e) => setRsvpData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your@email.com"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phone (optional)</label>
                <Input
                  value={rsvpData.phone}
                  onChange={(e) => setRsvpData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+44..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Number of Attendees</label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={rsvpData.num_attendees}
                  onChange={(e) => setRsvpData(prev => ({ ...prev, num_attendees: parseInt(e.target.value) || 1 }))}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes (optional)</label>
                <Textarea
                  value={rsvpData.notes}
                  onChange={(e) => setRsvpData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any special requirements or questions..."
                  rows={3}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Confirm RSVP
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowRSVP(false);
                    setSelectedEvent(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default EventsPage;
